import React, { useEffect, useState } from 'react';
import { BarChart3, Building2, CheckCircle2, CreditCard, Loader2, Palette, Save, X } from 'lucide-react';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import { getSettings, saveSettings } from '../services/analytics';
import {
  cancelSubscription,
  formatLimit,
  getBillingSummary as fetchBillingSummary,
  getSubscriptionErrorMessage,
  type BillingSummary,
} from '../services/subscription';

const SettingsPage: React.FC = () => {
  const [settings, setSettings] = useState(getSettings());
  const [saved, setSaved] = useState(false);
  const [billingSummary, setBillingSummary] = useState<BillingSummary | null>(null);
  const [billingError, setBillingError] = useState('');
  const [billingLoading, setBillingLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);

  const loadBilling = async () => {
    setBillingLoading(true);
    setBillingError('');

    try {
      setBillingSummary(await fetchBillingSummary());
    } catch (error) {
      setBillingError(getSubscriptionErrorMessage(error));
    } finally {
      setBillingLoading(false);
    }
  };

  useEffect(() => {
    loadBilling();
  }, []);

  const handleSave = () => {
    saveSettings(settings);
    setSaved(true);
    window.setTimeout(() => setSaved(false), 2000);
  };

  const handleCancelSubscription = async () => {
    const confirmed = window.confirm('Cancel this subscription at the end of the paid period?');
    if (!confirmed) return;

    setCanceling(true);
    setBillingError('');

    try {
      const result = await cancelSubscription();
      setBillingSummary(result.billing);
    } catch (error) {
      setBillingError(getSubscriptionErrorMessage(error));
    } finally {
      setCanceling(false);
    }
  };

  return (
    <div className="page-stack" style={{ maxWidth: '900px', margin: '0 auto' }}>
      <header className="page-header">
        <div>
          <h1 style={{ fontSize: '2.2rem', marginBottom: '0.55rem' }}>Settings</h1>
          <p className="page-kicker">Choose how GabMic formats and displays your analytics.</p>
        </div>
        {saved && (
          <div className="premium-card" style={{ padding: '0.85rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <CheckCircle2 size={18} color="var(--status-success)" />
            <span>Settings saved</span>
            <X size={17} color="var(--text-secondary)" />
          </div>
        )}
      </header>
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
        <SettingsSection icon={Building2} title="Business">
          <div className="settings-row">
            <div>
              <strong>Business name</strong>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>This is the name of your business.</p>
            </div>
            <Input
              type="text"
              placeholder="Your business"
              value={settings.businessName}
              onChange={(event) => setSettings({ ...settings, businessName: event.target.value })}
            />
          </div>
        </SettingsSection>

        <SettingsSection icon={BarChart3} title="Data display">
          <div className="settings-row">
            <div>
              <strong>Default currency</strong>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>Choose the currency used in your analytics.</p>
            </div>
            <Select
              value={settings.currency}
              onChange={(event) => setSettings({ ...settings, currency: event.target.value })}
            >
              <option value="USD">USD - US Dollar</option>
              <option value="GBP">GBP - British Pound</option>
              <option value="EUR">EUR - Euro</option>
              <option value="NGN">NGN - Nigerian Naira</option>
              <option value="INR">INR - Indian Rupee</option>
              <option value="GHS">GHS - Ghanaian Cedi</option>
            </Select>
          </div>
        </SettingsSection>

        <SettingsSection icon={CreditCard} title="Billing">
          {billingLoading ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7rem', color: 'var(--text-secondary)' }}>
              <Loader2 size={18} style={{ animation: 'gabmic-spin 1s linear infinite' }} />
              Loading billing details...
            </div>
          ) : billingError ? (
            <div className="settings-row">
              <div>
                <strong>Billing unavailable</strong>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>{billingError}</p>
              </div>
              <Button variant="outline" onClick={loadBilling}>Try again</Button>
            </div>
          ) : billingSummary ? (
            <div style={{ display: 'grid', gap: '1rem' }}>
              <div className="settings-row">
                <div>
                  <strong>{billingSummary.customer.planName} plan</strong>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
                    {formatBillingStatus(billingSummary.customer.status)}
                    {billingSummary.customer.currency ? ` · ${billingSummary.customer.currency}` : ''}
                  </p>
                </div>
                <a href="/pricing">
                  <Button variant="outline" fullWidth>View pricing</Button>
                </a>
              </div>

              <div className="settings-row">
                <div>
                  <strong>Monthly usage</strong>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
                    {billingSummary.usage.monthKey} billing month
                  </p>
                </div>
                <div style={{ display: 'grid', gap: '0.45rem', color: 'var(--text-secondary)', fontSize: '0.92rem' }}>
                  <span>
                    Uploads: {billingSummary.usage.used.uploads.toLocaleString()} used · {formatLimit(billingSummary.usage.remaining.uploads, 'remaining')}
                  </span>
                  <span>
                    AI questions: {billingSummary.usage.used.aiQuestions.toLocaleString()} used · {formatLimit(billingSummary.usage.remaining.aiQuestions, 'remaining')}
                  </span>
                </div>
              </div>

              <div className="settings-row">
                <div>
                  <strong>Renewal</strong>
                  <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
                    {billingSummary.customer.cancelAtPeriodEnd
                      ? 'This subscription is set to end at the paid-period close.'
                      : 'Paystack manages renewal for paid subscriptions.'}
                  </p>
                </div>
                <div style={{ display: 'grid', gap: '0.45rem', color: 'var(--text-secondary)', fontSize: '0.92rem' }}>
                  <span>Next payment: {formatDate(billingSummary.customer.nextPaymentDate)}</span>
                  <span>Current period ends: {formatDate(billingSummary.customer.currentPeriodEnd)}</span>
                </div>
              </div>

              {billingSummary.customer.paystackSubscriptionCode && billingSummary.customer.status === 'active' && (
                <div className="settings-row">
                  <div>
                    <strong>Cancel subscription</strong>
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>
                      Keep paid access until Paystack confirms the paid period has ended.
                    </p>
                  </div>
                  <Button variant="outline" onClick={handleCancelSubscription} disabled={canceling}>
                    {canceling ? 'Cancelling...' : 'Cancel renewal'}
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </SettingsSection>

        <SettingsSection icon={Palette} title="Appearance">
          <div className="settings-row">
            <div>
              <strong>Theme</strong>
              <p style={{ margin: '0.25rem 0 0', fontSize: '0.9rem' }}>GabMic is tuned for a premium dark interface.</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid var(--border-color)', borderRadius: '8px', overflow: 'hidden' }}>
              <button type="button" style={segmentStyle(true)}>Dark</button>
              <button type="button" style={segmentStyle(false)}>System</button>
            </div>
          </div>
        </SettingsSection>

        <div>
          <Button variant="primary" onClick={handleSave}>
            <Save size={17} />
            Save changes
          </Button>
          <p style={{ margin: '0.75rem 0 0', fontSize: '0.9rem' }}>Changes apply to future analytics.</p>
        </div>
      </div>
    </div>
  );
};

interface SettingsSectionProps {
  icon: React.ElementType;
  title: string;
  children: React.ReactNode;
}

const SettingsSection: React.FC<SettingsSectionProps> = ({ icon: Icon, title, children }) => {
  return (
    <section className="premium-card" style={{ padding: '1.2rem', display: 'grid', gridTemplateColumns: '48px 1fr', gap: '1rem', alignItems: 'start' }}>
      <span className="icon-tile">
        <Icon size={20} />
      </span>
      <div>
        <h3 className="section-title" style={{ marginBottom: '1.2rem' }}>{title}</h3>
        {children}
      </div>
    </section>
  );
};

const segmentStyle = (active: boolean): React.CSSProperties => ({
  padding: '0.8rem 1rem',
  background: active ? 'rgba(255,255,255,0.08)' : 'transparent',
  border: 0,
  color: active ? 'var(--text-main)' : 'var(--text-secondary)',
  font: 'inherit',
  cursor: 'pointer',
});

const formatDate = (value: string | null) => {
  if (!value) return 'Not available yet';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not available yet';
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const formatBillingStatus = (status: string) => {
  if (status === 'cancel_at_period_end') return 'Cancels at period end';
  if (status === 'past_due') return 'Payment needs attention';
  if (status === 'active') return 'Active';
  if (status === 'cancelled') return 'Cancelled';
  return 'Free';
};

export default SettingsPage;
